import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  /** Label displayed above the input */
  label?: string;
  /** Hint text shown next to the label */
  hint?: string;
  /** Maximum number of tags allowed */
  max?: number;
  placeholder?: string;
}

export function TagInput({
  tags,
  onChange,
  label,
  hint,
  max,
  placeholder = 'Add item...',
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const atLimit = max !== undefined && tags.length >= max;

  const addTag = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (atLimit) return;
    if (tags.some((t) => t.toLowerCase() === trimmed.toLowerCase())) return;
    onChange([...tags, trimmed]);
    setInputValue('');
  };

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    }
    if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-surface-700 mb-1">
          {label}
          {hint && <span className="text-surface-400 font-normal ml-1">({hint})</span>}
        </label>
      )}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-surface-200 bg-white px-3 py-2 focus-within:border-primary-400 focus-within:ring-1 focus-within:ring-primary-400 transition-colors">
        {tags.map((tag, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 rounded-full bg-primary-50 border border-primary-200 px-2.5 py-0.5 text-xs font-medium text-primary-800"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(i)}
              className="ml-0.5 rounded-full p-0.5 hover:bg-primary-200 transition-colors"
              aria-label={`Remove ${tag}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {!atLimit && (
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => addTag(inputValue)}
            placeholder={tags.length === 0 ? placeholder : 'Add more...'}
            className="flex-1 min-w-[120px] bg-transparent text-sm text-surface-900 outline-none placeholder:text-surface-400"
          />
        )}
      </div>
      {atLimit && (
        <p className="text-xs text-surface-400 mt-1">Maximum {max} items reached</p>
      )}
    </div>
  );
}
